/**
 * MicPolarOrb — organic SVG "blob" that morphs to the mic level.
 *
 * Replaces the boring flat amplitude bar (``Waveform``). Twelve
 * vertices are placed on a circle in polar coordinates; each vertex's
 * radius pulses with its own phase, modulated by the user's live RMS.
 * Smooth Catmull-Rom-style bezier curves between vertices give a
 * fluid, liquid-breathing look that feels alive even at low signal
 * levels — there's a baseline ambient motion so the shape never goes
 * fully static.
 *
 * Production characteristics:
 *
 *   - All animation runs through ``requestAnimationFrame``; React
 *     never sees per-frame state updates. The RMS prop is read
 *     directly each tick (no useEffect dep churn).
 *   - The vertex math + ``d`` string rebuild is ~12 floats and a
 *     ~120-char string per frame — cheap enough at 60 fps to leave
 *     the audio thread alone.
 *   - Square corners + tokens-only colours per ``Docs/DESIGN.md``.
 *
 * Inspired by the polar-polygon vertex distortion approach
 * (https://github.com/gesoto/microphone-audio-visualizer) but rewritten
 * for our token system + React-friendly rAF lifecycle.
 */
import * as React from "react";
import { cn } from "@/lib/cn";

const VERTEX_COUNT = 12;
// Each vertex gets its own phase offset and "weight" so the shape
// morphs organically rather than uniformly pulsing in/out. Computed
// once at module load — the values are deterministic by index, no
// Math.random side-effects that would change between renders.
const PHASES: number[] = Array.from(
  { length: VERTEX_COUNT },
  (_, i) => (i / VERTEX_COUNT) * Math.PI * 2,
);
const WEIGHTS: number[] = Array.from(
  { length: VERTEX_COUNT },
  (_, i) => 0.6 + 0.4 * Math.sin(i * 1.7),
);

export interface MicPolarOrbProps {
  /** Latest level in [0, 1] (the LiveKit mic level). */
  rms: number;
  /** Override the SVG side length (in CSS pixels). Defaults to 96. */
  size?: number;
  /** Optional className. */
  className?: string;
}

export function MicPolarOrb({
  rms,
  size = 96,
  className,
}: MicPolarOrbProps) {
  // Keep the live rms in a ref so the rAF loop can read the latest
  // without subscribing to it as an effect dep (which would tear down
  // and rebuild the loop on every audio frame — 50× per second).
  const rmsRef = React.useRef(rms);
  rmsRef.current = rms;

  const pathRef = React.useRef<SVGPathElement | null>(null);
  const fillRef = React.useRef<SVGPathElement | null>(null);

  React.useEffect(() => {
    let raf = 0;
    let t0 = performance.now();
    // Internal smoothed RMS so a single loud sample doesn't yank the
    // shape; gentle EMA towards the live value each frame.
    let smoothed = 0;
    const half = size / 2;

    const tick = () => {
      const now = performance.now();
      const t = (now - t0) * 0.001; // seconds
      smoothed = smoothed * 0.7 + rmsRef.current * 0.3;
      // Map RMS into a radius perturbation. Floor at 0.08 so the shape
      // is always slightly off-circle, never a dead disc.
      const energy = Math.max(0.08, Math.min(0.7, smoothed * 3.5));

      // Each vertex's radius = base + per-vertex morph + global pulse.
      // Pulse frequency is intentionally low so the shape doesn't read
      // as "jittery" — it breathes.
      const baseR = half * 0.62;
      const maxDelta = half * 0.18;
      const pts: { x: number; y: number }[] = [];
      for (let i = 0; i < VERTEX_COUNT; i++) {
        const a = (i / VERTEX_COUNT) * Math.PI * 2;
        const morph =
          Math.sin(t * 1.4 + PHASES[i]) * 0.4 +
          Math.cos(t * 0.7 + PHASES[i] * 1.3) * 0.6;
        const r = baseR + maxDelta * energy * WEIGHTS[i] * morph;
        pts.push({ x: half + Math.cos(a) * r, y: half + Math.sin(a) * r });
      }

      // Build a smooth closed SVG path via Catmull-Rom → cubic bezier.
      // Standard formula with tension = 0.5 gives the "rounded organic"
      // shape we want without overshoot.
      const d: string[] = [`M ${pts[0].x.toFixed(2)} ${pts[0].y.toFixed(2)}`];
      for (let i = 0; i < VERTEX_COUNT; i++) {
        const p0 = pts[(i - 1 + VERTEX_COUNT) % VERTEX_COUNT];
        const p1 = pts[i];
        const p2 = pts[(i + 1) % VERTEX_COUNT];
        const p3 = pts[(i + 2) % VERTEX_COUNT];
        const c1x = p1.x + (p2.x - p0.x) / 6;
        const c1y = p1.y + (p2.y - p0.y) / 6;
        const c2x = p2.x - (p3.x - p1.x) / 6;
        const c2y = p2.y - (p3.y - p1.y) / 6;
        d.push(
          `C ${c1x.toFixed(2)} ${c1y.toFixed(2)} ` +
            `${c2x.toFixed(2)} ${c2y.toFixed(2)} ` +
            `${p2.x.toFixed(2)} ${p2.y.toFixed(2)}`,
        );
      }
      d.push("Z");
      const ds = d.join(" ");
      pathRef.current?.setAttribute("d", ds);
      fillRef.current?.setAttribute("d", ds);

      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [size]);

  return (
    <svg
      role="meter"
      aria-label="microphone activity"
      aria-valuenow={Math.round(rms * 100)}
      aria-valuemin={0}
      aria-valuemax={100}
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className={cn("block", className)}
    >
      {/* Glow halo behind the blob — subtle, pulses with the same path. */}
      <defs>
        <radialGradient id="mpo-halo" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="var(--om-color-accent)" stopOpacity="0.35" />
          <stop offset="60%" stopColor="var(--om-color-accent)" stopOpacity="0.08" />
          <stop offset="100%" stopColor="var(--om-color-accent)" stopOpacity="0" />
        </radialGradient>
      </defs>
      <circle cx={size / 2} cy={size / 2} r={size / 2} fill="url(#mpo-halo)" />
      {/* Filled blob — soft accent fill. */}
      <path
        ref={fillRef}
        d=""
        fill="var(--om-color-accent)"
        fillOpacity="0.18"
      />
      {/* Stroked outline on top for crispness. */}
      <path
        ref={pathRef}
        d=""
        fill="none"
        stroke="var(--om-color-accent)"
        strokeWidth={1.5}
        strokeLinejoin="round"
      />
    </svg>
  );
}
